// =============================================
// uploadQueueStore — the upload queue's memory across a reload or a crash
// =============================================
// The files a user hands to Upload Docs used to exist only in the page's memory: if
// the tab crashed, was closed or reloaded half-way through a 177-page scan, the pages
// not yet saved were simply gone, with nothing on screen to say so.
//
// Every waiting file is therefore kept here — the picked File itself plus the context
// it was added with — until it is completely saved on the server. When the app opens
// again, uploadQueue.restoreSavedUploads() puts them back in the queue and carries on.
//
// This is a WORK-IN-PROGRESS buffer, not storage: a record is deleted the moment its
// file is fully on the server (the File Storage Rule — the durable copy is the server's).
// Records belong to the login that added them and are dropped after MAX_AGE_MS.
// Everything here is best-effort: without IndexedDB the queue simply works as before.
// =============================================

import type { SplitPlan } from './pageGrouping';

const DB_NAME = 'akt_upload_queue';
const STORE = 'jobs';
const DB_VERSION = 1;

/** A file left this long is no longer resumed (the user has moved on). */
export const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export interface SavedUpload {
  id: string;
  /** login that added it (session user id) — never resumed under another login */
  userId: string;
  file: File;
  name: string;
  size: number;
  type: string;
  addedAt: string;
  convertedFrom?: string;
  ctx: Record<string, any>;           // UploadContext (kept loose: this file knows nothing of the queue)
  status: 'queued' | 'review' | 'error';
  errorMsg?: string;
  split?: SplitPlan;                  // auto-separate: the suggested split awaiting its check
  confirmedGroups?: number[][];       // auto-separate: the split the user approved
  savedAt: number;
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') { reject(new Error('IndexedDB unavailable')); return; }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE, { keyPath: 'id' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  dbPromise.catch(() => { dbPromise = null; });
  return dbPromise;
}

function request<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then(db => new Promise<T>((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const req = run(tx.objectStore(STORE));
    tx.oncomplete = () => resolve(req.result);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  }));
}

/** Keep (or update) a waiting file. Never throws. */
export async function saveUpload(record: SavedUpload): Promise<void> {
  try { await request('readwrite', s => s.put(record)); } catch { /* best-effort */ }
}

/** Forget a file — it is fully saved, or the user removed it. Never throws. */
export async function forgetUpload(id: string): Promise<void> {
  try { await request('readwrite', s => s.delete(id)); } catch { /* best-effort */ }
}

/** The files this login left unfinished, oldest first. Too-old ones are dropped. */
export async function loadUploads(userId: string): Promise<SavedUpload[]> {
  try {
    const all = await request<SavedUpload[]>('readonly', s => s.getAll());
    const now = Date.now();
    const mine: SavedUpload[] = [];
    for (const r of all) {
      if (now - (r.savedAt || 0) > MAX_AGE_MS) { void forgetUpload(r.id); continue; }
      if (String(r.userId) === String(userId) && r.file) mine.push(r);
    }
    return mine.sort((a, b) => String(a.addedAt).localeCompare(String(b.addedAt)));
  } catch {
    return [];
  }
}
